import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  Settings,
  User,
  Bell,
  Shield,
  Palette,
  Download,
  Upload,
  Trash2,
  Save,
  RefreshCw,
  Bot,
  Zap,
  Clock,
  CreditCard,
  ExternalLink,
  Loader2,
  ShieldCheck,
  Star
} from 'lucide-react';
import { DashboardLayoutSwitcher } from '@/components/dashboard/DashboardLayoutSwitcher';
import { useUserPreferences } from '@/hooks/useUserPreferences';
import { useAnalyticsHints } from '@/hooks/useAnalyticsHints';
import { useToast } from '@/hooks/use-toast';
import { DigestSettings } from '@/components/dashboard/DigestSettings';
import { PlanUsageCard } from '@/components/dashboard/PlanUsageCard';
import ProfileTab from '@/components/dashboard/ProfileTab';
import AIAssistantSettings from '@/components/dashboard/AIAssistantSettings';
import BetaUsageCard from '@/components/beta/BetaUsageCard';
import AppearanceSettings from '@/components/dashboard/AppearanceSettings';
import FamilySettings from '@/components/dashboard/FamilySettings';
import FeedSovereigntySettings from '@/components/dashboard/FeedSovereigntySettings';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Link } from 'react-router-dom';

const DashboardSettings: React.FC = () => {
  const { preferences, savePreferences } = useUserPreferences();
  const { toast } = useToast();
  const { user } = useAuth();
  const { hintsEnabled, setHints } = useAnalyticsHints();
  const [isLoading, setIsLoading] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  // X Feed Frequency
  const [twitterInterval, setTwitterInterval] = useState<24 | 12>(24);
  const [xEnhancedActive, setXEnhancedActive] = useState(false);
  const [xFreqLoading, setXFreqLoading] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    supabase
      .from('profiles')
      .select('twitter_fetch_interval_hours')
      .eq('id', user.id)
      .maybeSingle()
      .then(({ data }) => {
        const h = data?.twitter_fetch_interval_hours ?? 24;
        setTwitterInterval(h === 12 ? 12 : 24);
        setXEnhancedActive(h === 12);
      });
  }, [user?.id]);

  const handleEnableXEnhanced = async () => {
    setXFreqLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { tier: 'x_enhanced' },
      });
      if (error || !data?.url) {
        toast({ variant: 'destructive', title: 'Could not start checkout', description: 'Please try again.' });
        return;
      }
      window.location.href = data.url;
    } catch {
      toast({ variant: 'destructive', title: 'Something went wrong', description: 'Please try again.' });
    } finally {
      setXFreqLoading(false);
    }
  };

  // Local state for form fields
  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    pushNotifications: true,
    weeklyReports: true,
    realTimeAlerts: false,
    mentionAlerts: true,
    sentimentAlerts: false
  });

  const [privacySettings, setPrivacySettings] = useState({
    dataRetention: '365',
    anonymizeData: false,
    shareAnalytics: true,
    exportFormat: 'json'
  });

  // ── Feed visibility / username (saved to profiles) ────────────────────────
  const [feedIsPublic, setFeedIsPublic] = useState(false);
  const [username, setUsername] = useState('');
  const [usernameError, setUsernameError] = useState('');
  const [feedPrivacyLoading, setFeedPrivacyLoading] = useState(false);
  const [feedPrivacyReady, setFeedPrivacyReady] = useState(false);

  // Billing state
  const [currentTier, setCurrentTier] = useState<string | null>(null);
  const [isFounding, setIsFounding] = useState(false);
  const [billingLoading, setBillingLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    supabase
      .from('profiles')
      .select('feed_is_public, username, subscription_tier, is_founding_member')
      .eq('id', user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (data) {
          setFeedIsPublic(data.feed_is_public ?? false);
          setUsername(data.username ?? '');
          setCurrentTier(data.subscription_tier ?? 'free');
          setIsFounding(Boolean(data.is_founding_member));
        }
        setFeedPrivacyReady(true);
        setBillingLoading(false);
      });
  }, [user?.id]);

  const openBillingPortal = async () => {
    setPortalLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('customer-portal');
      if (error || !data?.url) {
        toast({ title: 'Could not open billing portal', description: 'Please try again.', variant: 'destructive' });
        return;
      }
      window.location.href = data.url;
    } catch {
      toast({ title: 'Something went wrong', description: 'Please try again.', variant: 'destructive' });
    } finally {
      setPortalLoading(false);
    }
  };

  const saveFeedVisibility = async () => {
    if (!user?.id) return;
    const trimmed = username.trim().toLowerCase();

    // Validate username format
    if (trimmed && !/^[a-z0-9_]{2,30}$/.test(trimmed)) {
      setUsernameError('Username must be 2–30 characters: lowercase letters, numbers, and underscores only.');
      return;
    }
    setUsernameError('');
    setFeedPrivacyLoading(true);

    const patch: Record<string, unknown> = { feed_is_public: feedIsPublic };
    if (trimmed) patch.username = trimmed;

    const { error } = await supabase
      .from('profiles')
      .update(patch)
      .eq('id', user.id);

    setFeedPrivacyLoading(false);

    if (error) {
      if (error.message?.includes('username')) {
        setUsernameError('That username is already taken. Try a different one.');
      } else {
        toast({ variant: 'destructive', title: 'Save failed', description: error.message });
      }
      return;
    }

    toast({ title: 'Feed visibility saved.' });
  };

  // Mark dirty whenever notification or privacy state changes
  const updateNotification = (patch: Partial<typeof notificationSettings>) => {
    setNotificationSettings(s => ({ ...s, ...patch }));
    setIsDirty(true);
  };
  const updatePrivacy = (patch: Partial<typeof privacySettings>) => {
    setPrivacySettings(s => ({ ...s, ...patch }));
    setIsDirty(true);
  };

  const handleSaveAll = async () => {
    setIsLoading(true);
    try {
      await handleSaveNotifications();
      setIsDirty(false);
      toast({ title: 'Settings saved', description: 'All changes have been saved.' });
    } catch {
      toast({ variant: 'destructive', title: 'Save failed', description: 'Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveNotifications = async () => {
    setIsLoading(true);
    try {
      // Save notification settings
      toast({
        title: "Notifications updated",
        description: "Your notification preferences have been saved."
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Update failed",
        description: "Could not update notifications. Please try again."
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportData = async () => {
    setIsLoading(true);
    try {
      // TODO: Implement actual data export via Edge Function
      // await supabase.functions.invoke('export-user-data', { body: { format: privacySettings.exportFormat } });
      
      toast({
        title: "Export started",
        description: "Your data export is being prepared. You'll receive an email when ready."
      });
      
      // Simulate export process
      setTimeout(() => {
        toast({
          title: "Export ready",
          description: "Your data has been exported successfully. Check your email for the download link."
        });
      }, 3000);
      
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Export failed",
        description: "Could not export your data. Please try again or contact support."
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteAccount = () => {
    const confirmed = window.confirm(
      "Are you absolutely sure you want to delete your account? This action cannot be undone and will permanently delete all your data, connections, and settings."
    );
    
    if (confirmed) {
      const doubleConfirm = window.confirm(
        "This is your final confirmation. Type 'DELETE' in the next prompt to confirm account deletion."
      );
      
      if (doubleConfirm) {
        const typeConfirm = window.prompt("Please type 'DELETE' to confirm:");
        
        if (typeConfirm === 'DELETE') {
          toast({
            variant: "destructive",
            title: "Account deletion initiated",
            description: "Your account deletion request has been submitted. You'll receive a confirmation email within 24 hours."
          });
          // TODO: Implement actual account deletion via Edge Function
          // await supabase.functions.invoke('delete-user-account', { body: { userId: user.id } });
        } else {
          toast({
            title: "Account deletion cancelled",
            description: "Account deletion was cancelled due to incorrect confirmation."
          });
        }
      }
    }
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">Settings</h1>
        <p className="text-muted-foreground">
          Manage your account, preferences, and dashboard configuration
        </p>
      </div>

      <Tabs defaultValue="profile" className="space-y-6">
        <TabsList className="grid w-full grid-cols-9">
          <TabsTrigger value="profile">Profile</TabsTrigger>
          <TabsTrigger value="appearance">Appearance</TabsTrigger>
          <TabsTrigger value="family">Family</TabsTrigger>
          <TabsTrigger value="ai-assistant">AI Assistant</TabsTrigger>
          <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="notifications">Notifications</TabsTrigger>
          <TabsTrigger value="privacy">Privacy</TabsTrigger>
          <TabsTrigger value="billing">Billing</TabsTrigger>
          <TabsTrigger value="advanced">Advanced</TabsTrigger>
        </TabsList>

        {/* Profile Settings */}
        <TabsContent value="profile">
          <div className="space-y-6">
            <BetaUsageCard />
            <ProfileTab />
          </div>
        </TabsContent>

        {/* Appearance */}
        <TabsContent value="appearance">
          <AppearanceSettings />
        </TabsContent>

        {/* Family */}
        <TabsContent value="family">
          <FamilySettings />
        </TabsContent>

        {/* AI Assistant Settings */}
        <TabsContent value="ai-assistant">
          <AIAssistantSettings />
        </TabsContent>

        {/* Dashboard Settings */}
        <TabsContent value="dashboard">
          <Card>
            <CardHeader>
              <CardTitle>Dashboard Layout</CardTitle>
            </CardHeader>
            <CardContent>
              <DashboardLayoutSwitcher 
                currentLayout={preferences?.dashboard_layout || 'overview'}
              />
            </CardContent>
          </Card>

          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Display Preferences</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label>Dark Mode</Label>
                  <p className="text-sm text-muted-foreground">Toggle dark/light theme</p>
                </div>
                <Switch />
              </div>
              
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label>Compact Mode</Label>
                  <p className="text-sm text-muted-foreground">Reduce spacing for more content</p>
                </div>
                <Switch />
              </div>
              
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label>Auto-refresh</Label>
                  <p className="text-sm text-muted-foreground">Automatically update data</p>
                </div>
                <Switch defaultChecked />
              </div>

              <div className="flex items-center justify-between pt-2 border-t">
                <div className="space-y-1">
                  <Label>Show analytics hints</Label>
                  <p className="text-sm text-muted-foreground">
                    Display explanations when you hover over metric tiles on the Analytics page
                  </p>
                </div>
                <Switch
                  checked={hintsEnabled}
                  onCheckedChange={setHints}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notifications */}
        <TabsContent value="notifications">
          <Card>
            <CardHeader>
              <CardTitle>Notification Preferences</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label>Email Notifications</Label>
                    <p className="text-sm text-muted-foreground">Receive updates via email</p>
                  </div>
                  <Switch 
                    checked={notificationSettings.emailNotifications}
                    onCheckedChange={(checked) => 
                      setNotificationSettings({...notificationSettings, emailNotifications: checked})
                    }
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label>Push Notifications</Label>
                    <p className="text-sm text-muted-foreground">Browser notifications</p>
                  </div>
                  <Switch 
                    checked={notificationSettings.pushNotifications}
                    onCheckedChange={(checked) => 
                      setNotificationSettings({...notificationSettings, pushNotifications: checked})
                    }
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label>Weekly Reports</Label>
                    <p className="text-sm text-muted-foreground">Summary of your social media activity</p>
                  </div>
                  <Switch 
                    checked={notificationSettings.weeklyReports}
                    onCheckedChange={(checked) => 
                      setNotificationSettings({...notificationSettings, weeklyReports: checked})
                    }
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label>Real-time Alerts</Label>
                    <p className="text-sm text-muted-foreground">Instant notifications for important events</p>
                  </div>
                  <Switch 
                    checked={notificationSettings.realTimeAlerts}
                    onCheckedChange={(checked) => 
                      setNotificationSettings({...notificationSettings, realTimeAlerts: checked})
                    }
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label>Mention Alerts</Label>
                    <p className="text-sm text-muted-foreground">Get notified when you're mentioned</p>
                  </div>
                  <Switch 
                    checked={notificationSettings.mentionAlerts}
                    onCheckedChange={(checked) => 
                      setNotificationSettings({...notificationSettings, mentionAlerts: checked})
                    }
                  />
                </div>
              </div>

              <Button onClick={handleSaveNotifications} disabled={isLoading}>
                <Save className="h-4 w-4 mr-2" />
                Save Notification Settings
              </Button>
            </CardContent>
          </Card>
          <DigestSettings />
        </TabsContent>

        {/* Privacy & Security */}
        <TabsContent value="privacy">
          <div className="space-y-6">

            {/* Feed visibility — the main new control */}
            <Card>
              <CardHeader>
                <CardTitle>Feed Visibility</CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Your feed is private by default. Only you can see it. Enable public mode to
                  share your curated stream with others — your rules and vault remain private,
                  only the posts that passed your filters are visible.
                </p>
              </CardHeader>
              <CardContent className="space-y-5">
                {/* Public toggle */}
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Make my feed public</Label>
                    <p className="text-xs text-muted-foreground">
                      Others can find you in{' '}
                      <Link to="/dashboard/explore" className="underline underline-offset-4 hover:text-foreground">
                        Explore
                      </Link>{' '}
                      and follow your curated stream.
                    </p>
                  </div>
                  <Switch
                    checked={feedIsPublic}
                    disabled={!feedPrivacyReady}
                    onCheckedChange={(v) => { setFeedIsPublic(v); setIsDirty(true); }}
                  />
                </div>

                {/* Username */}
                <div className="space-y-1.5">
                  <Label htmlFor="username-field">
                    Your @username{' '}
                    <span className="text-muted-foreground font-normal">(required for a public profile)</span>
                  </Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm select-none">
                      @
                    </span>
                    <Input
                      id="username-field"
                      value={username}
                      onChange={(e) => {
                        setUsername(e.target.value);
                        setUsernameError('');
                        setIsDirty(true);
                      }}
                      placeholder="yourhandle"
                      className="pl-7"
                      maxLength={30}
                      disabled={!feedPrivacyReady}
                    />
                  </div>
                  {usernameError && (
                    <p className="text-xs text-destructive">{usernameError}</p>
                  )}
                  {username && !usernameError && (
                    <p className="text-xs text-muted-foreground">
                      Your public profile will be at{' '}
                      <Link
                        to={`/u/${username.toLowerCase()}`}
                        className="underline underline-offset-4 hover:text-foreground"
                      >
                        socialscoop.ai/u/{username.toLowerCase()}
                      </Link>
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Lowercase letters, numbers, and underscores. 2–30 characters.
                  </p>
                </div>

                {/* What's visible note */}
                {feedIsPublic && (
                  <div className="rounded-lg bg-muted/50 border border-border p-4 text-sm text-muted-foreground space-y-1">
                    <p className="font-medium text-foreground text-sm">What others can see:</p>
                    <p>✓ Posts that passed your Listening Rules (your curated feed)</p>
                    <p>✓ Your name, username, bio, and follower count</p>
                    <p className="text-muted-foreground">✗ Your Fences, rules, or Vault content — always private</p>
                    <p className="text-muted-foreground">✗ Which platforms you're connected to</p>
                  </div>
                )}

                <Button
                  size="sm"
                  onClick={saveFeedVisibility}
                  disabled={feedPrivacyLoading || !feedPrivacyReady}
                  className="bg-[#1A2E4A] hover:bg-[#1A2E4A]/90 text-white"
                >
                  {feedPrivacyLoading ? 'Saving…' : 'Save visibility settings'}
                </Button>
              </CardContent>
            </Card>

            <FeedSovereigntySettings />
          </div>

          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Privacy & Security</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Data Retention Period</Label>
                  <Select value={privacySettings.dataRetention} onValueChange={(value) => 
                    setPrivacySettings({...privacySettings, dataRetention: value})
                  }>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="30">30 days</SelectItem>
                      <SelectItem value="90">90 days</SelectItem>
                      <SelectItem value="365">1 year</SelectItem>
                      <SelectItem value="forever">Forever</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label>Anonymize Data</Label>
                    <p className="text-sm text-muted-foreground">Remove personal identifiers from analytics</p>
                  </div>
                  <Switch 
                    checked={privacySettings.anonymizeData}
                    onCheckedChange={(checked) => 
                      setPrivacySettings({...privacySettings, anonymizeData: checked})
                    }
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label>Share Analytics</Label>
                    <p className="text-sm text-muted-foreground">Help improve the service with usage data</p>
                  </div>
                  <Switch 
                    checked={privacySettings.shareAnalytics}
                    onCheckedChange={(checked) => 
                      setPrivacySettings({...privacySettings, shareAnalytics: checked})
                    }
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Billing */}
        <TabsContent value="billing">
          <div className="space-y-5">

            {/* Current plan */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-4 w-4" />
                  Current Plan
                </CardTitle>
              </CardHeader>
              <CardContent>
                {billingLoading ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading…
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">
                          {isFounding
                            ? 'Founding Member'
                            : currentTier && currentTier !== 'free'
                              ? `${currentTier.replace('_annual', '').replace(/^\w/, c => c.toUpperCase())} plan`
                              : 'Free plan'}
                          {isFounding && (
                            <Star className="inline h-3.5 w-3.5 ml-1.5 text-amber-500 fill-amber-400" />
                          )}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {currentTier && currentTier !== 'free'
                            ? 'Active subscription'
                            : 'Upgrade to unlock Reddit, X, and more.'}
                        </p>
                      </div>
                      {currentTier && currentTier !== 'free' ? (
                        <Badge className="bg-[#1A2E4A] text-white">Active</Badge>
                      ) : (
                        <Badge variant="outline">Free</Badge>
                      )}
                    </div>

                    {currentTier && currentTier !== 'free' ? (
                      <div className="flex flex-col sm:flex-row gap-2 pt-2 border-t">
                        <Button
                          variant="outline"
                          onClick={openBillingPortal}
                          disabled={portalLoading}
                          className="w-full sm:w-auto"
                        >
                          {portalLoading
                            ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                            : <ExternalLink className="h-4 w-4 mr-1.5" />
                          }
                          Manage billing &amp; payment method
                        </Button>
                        <Button
                          variant="outline"
                          onClick={openBillingPortal}
                          disabled={portalLoading}
                          className="w-full sm:w-auto text-red-600 border-red-200 hover:bg-red-50"
                        >
                          Cancel subscription
                        </Button>
                      </div>
                    ) : (
                      <div className="pt-2 border-t">
                        <Button asChild className="bg-[#1A2E4A] hover:bg-[#1A2E4A]/90 text-white">
                          <Link to="/dashboard/upgrade">View plans &amp; upgrade</Link>
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Usage meters */}
            <PlanUsageCard currentTier={currentTier} />

            {/* Data commitment */}
            <div className="rounded-xl border border-[#1A2E4A]/15 bg-[#1A2E4A]/[0.03] px-5 py-4 flex items-start gap-3">
              <ShieldCheck className="h-5 w-5 text-[#1A2E4A] shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-[#1A2E4A]">You pay for the product. We never sell your data.</p>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                  Your subscription is the entire business model. There are no ads, no data brokers,
                  and no third-party tracking. What you read, filter, and care about stays yours.
                </p>
              </div>
            </div>

            {/* FAQ */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                  Billing FAQ
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-sm">
                <div>
                  <p className="font-medium">How do I cancel?</p>
                  <p className="text-muted-foreground mt-0.5">
                    Click "Cancel subscription" above. You'll keep access until the end of your billing period. No questions asked.
                  </p>
                </div>
                <div>
                  <p className="font-medium">Can I switch between monthly and annual?</p>
                  <p className="text-muted-foreground mt-0.5">
                    Yes — click "Manage billing" to change your billing interval or update your payment method via the Stripe portal.
                  </p>
                </div>
                <div>
                  <p className="font-medium">What happens when I cancel?</p>
                  <p className="text-muted-foreground mt-0.5">
                    Your account reverts to the free tier at the end of the period. Your data, rules, and fences are preserved — you just lose access to paid platforms until you resubscribe.
                  </p>
                </div>
              </CardContent>
            </Card>

          </div>
        </TabsContent>

        {/* Advanced */}
        <TabsContent value="advanced">
          <div className="space-y-6">

            {/* X Feed Frequency */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  X (Twitter) Feed Frequency
                </CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  X API charges per tweet read. More frequent refreshes cost more — we pass
                  the difference on transparently as an optional add-on.
                </p>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Standard option */}
                <div
                  className={`rounded-lg border p-4 flex items-start justify-between gap-4 cursor-pointer transition-colors ${
                    twitterInterval === 24 ? 'border-foreground/30 bg-muted/30' : 'border-border hover:bg-muted/20'
                  }`}
                  onClick={() => !xEnhancedActive && setTwitterInterval(24)}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="font-medium text-sm">Standard</span>
                      <Badge variant="secondary" className="text-[10px] py-0 px-1.5">Included</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Refreshes once per day · ~25 posts · ~$3.75/mo API cost
                    </p>
                  </div>
                  <div className={`mt-1 h-4 w-4 rounded-full border-2 shrink-0 ${
                    twitterInterval === 24 ? 'border-foreground bg-foreground' : 'border-muted-foreground'
                  }`} />
                </div>

                {/* Enhanced option */}
                <div
                  className={`rounded-lg border p-4 flex items-start justify-between gap-4 ${
                    xEnhancedActive ? 'border-foreground/30 bg-muted/30 cursor-pointer' : 'border-border'
                  }`}
                  onClick={() => xEnhancedActive && setTwitterInterval(12)}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="font-medium text-sm">Enhanced</span>
                      {xEnhancedActive
                        ? <Badge className="text-[10px] py-0 px-1.5 bg-emerald-600">Active</Badge>
                        : <Badge variant="outline" className="text-[10px] py-0 px-1.5 text-muted-foreground">+$4/mo</Badge>
                      }
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Refreshes every 12 hours · ~25 posts · ~$7.50/mo API cost
                    </p>
                  </div>
                  {xEnhancedActive ? (
                    <div className={`mt-1 h-4 w-4 rounded-full border-2 shrink-0 ${
                      twitterInterval === 12 ? 'border-foreground bg-foreground' : 'border-muted-foreground'
                    }`} />
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      className="shrink-0"
                      onClick={(e) => { e.stopPropagation(); handleEnableXEnhanced(); }}
                      disabled={xFreqLoading}
                    >
                      {xFreqLoading ? <RefreshCw className="h-3 w-3 animate-spin" /> : (
                        <><Zap className="h-3 w-3 mr-1" />Enable</>
                      )}
                    </Button>
                  )}
                </div>

                <p className="text-xs text-muted-foreground pt-1">
                  Prices reflect actual X API costs. No markup — we charge exactly what X charges us.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>AI Lab</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-3">
                  Experimental tools for testing AI filtering rules and prompt behavior. Moved here
                  from the main navigation because it's not part of the everyday workflow.
                </p>
                <Button variant="outline" asChild>
                  <a href="/dashboard/ai-lab">Open AI Lab</a>
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Data Management</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Export Format</Label>
                  <Select value={privacySettings.exportFormat} onValueChange={(value) => 
                    setPrivacySettings({...privacySettings, exportFormat: value})
                  }>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="json">JSON</SelectItem>
                      <SelectItem value="csv">CSV</SelectItem>
                      <SelectItem value="xlsx">Excel</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex gap-2">
                  <Button variant="outline" onClick={handleExportData}>
                    <Download className="h-4 w-4 mr-2" />
                    Export Data
                  </Button>
                  <Button variant="outline">
                    <Upload className="h-4 w-4 mr-2" />
                    Import Data
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-red-600">Danger Zone</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 border border-red-200 rounded-lg bg-red-50">
                  <h4 className="font-medium text-red-800">Delete Account</h4>
                  <p className="text-sm text-red-600 mt-1">
                    Permanently delete your account and all associated data. This action cannot be undone.
                  </p>
                  <Button 
                    variant="destructive" 
                    className="mt-3"
                    onClick={handleDeleteAccount}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Account
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Sticky save bar — appears when any setting has been changed */}
      {isDirty && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-[#E5E7EB] px-6 py-3 flex items-center justify-between shadow-lg">
          <p className="text-sm text-muted-foreground">You have unsaved changes.</p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsDirty(false)}
              disabled={isLoading}
            >
              Discard
            </Button>
            <Button
              size="sm"
              onClick={handleSaveAll}
              disabled={isLoading}
              className="bg-[#1A2E4A] hover:bg-[#1A2E4A]/90 text-white"
            >
              <Save className="h-4 w-4 mr-2" />
              {isLoading ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardSettings;